import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles } from "../shared/styles";
import { LuFolderOpen, LuChevronRight } from "react-icons/lu";

export function MoveCopyModal({ fileMoveModal, siteId, currentFolderId, excludeFolderId, onCancel, onConfirm, processingFile }) {
  const [navPath, setNavPath] = useState([]); // [{id, name}], root-first, browsing state local to this modal
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [targetFolderId, setTargetFolderId] = useState(null);

  const loadLevel = (path) => {
    const parent = path[path.length - 1];
    setLoading(true);
    const parentParam = parent ? `&parent=${parent.id}` : "";
    axios.get(`${API}/site-folders/?site=${siteId}${parentParam}`).then((res) => {
      setItems(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    if (fileMoveModal) {
      setNavPath([]);
      setTargetFolderId(null);
      loadLevel([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileMoveModal]);

  if (!fileMoveModal) return null;
  const { item, mode, kind } = fileMoveModal;
  const isFolderMove = kind === "folder";

  const drillInto = (folder) => {
    const next = [...navPath, { id: folder.id, name: folder.name }];
    setNavPath(next);
    setTargetFolderId(folder.id);
    loadLevel(next);
  };

  const goUp = (index) => {
    const next = index < 0 ? [] : navPath.slice(0, index + 1);
    setNavPath(next);
    setTargetFolderId(next.length ? next[next.length - 1].id : null);
    loadLevel(next);
  };

  const visibleItems = items.filter((f) =>
    (mode === "copy" || f.id !== currentFolderId) &&
    (excludeFolderId == null || f.id !== excludeFolderId)
  );
  const destinationName = navPath.length ? navPath[navPath.length - 1].name : "Root";
  const confirmDisabled =
    (!isFolderMove && targetFolderId == null) ||
    processingFile ||
    (mode === "move" && targetFolderId === currentFolderId);

  return (
    <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ backgroundColor: colors.white, borderRadius: "12px", padding: "28px", width: "420px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <div style={{ fontSize: "16px", fontWeight: "700", color: colors.text, marginBottom: "4px" }}>
          {mode === "move" ? "Move to folder" : "Copy to folder"}
        </div>
        <div style={{ fontSize: "13px", color: colors.slate, marginBottom: "12px", wordBreak: "break-all" }}>
          {item.name}
        </div>

        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", fontSize: "12px", color: colors.slate, marginBottom: "10px", alignItems: "center" }}>
          <span
            style={{ cursor: "pointer", color: navPath.length ? colors.blue : colors.text, fontWeight: navPath.length ? "500" : "700" }}
            onClick={() => goUp(-1)}
          >
            Root
          </span>
          {navPath.map((c, i) => (
            <React.Fragment key={c.id}>
              <LuChevronRight size={12} />
              <span
                style={{ cursor: i < navPath.length - 1 ? "pointer" : "default", color: i < navPath.length - 1 ? colors.blue : colors.text, fontWeight: i < navPath.length - 1 ? "500" : "700" }}
                onClick={() => i < navPath.length - 1 && goUp(i)}
              >
                {c.name}
              </span>
            </React.Fragment>
          ))}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "260px", overflowY: "auto", marginBottom: "16px", border: `1px solid ${colors.border}`, borderRadius: "8px", padding: "6px" }}>
          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : visibleItems.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center", color: colors.slate, fontSize: "13px" }}>
              No subfolders here.
            </div>
          ) : (
            visibleItems.map((f) => {
              const isCurrentTarget = navPath[navPath.length - 1]?.id === f.id && targetFolderId === f.id;
              return (
                <div
                  key={f.id}
                  onClick={() => drillInto(f)}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px",
                    padding: "8px 10px", borderRadius: "6px", cursor: "pointer",
                    backgroundColor: isCurrentTarget ? "#f0f4ff" : "transparent",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                    <LuFolderOpen size={16} style={{ color: "#C8922A", flexShrink: 0 }} />
                    <span style={{ fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {f.name}
                    </span>
                  </div>
                  {f.subfolder_count > 0 && <LuChevronRight size={13} style={{ color: colors.slate, flexShrink: 0 }} />}
                </div>
              );
            })
          )}
        </div>

        <div style={{ fontSize: "12px", color: colors.slate, marginBottom: "12px" }}>
          Destination: <strong>{destinationName}</strong>
        </div>

        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{ padding: "8px 18px", borderRadius: "8px", border: `1px solid ${colors.border}`, background: "white", cursor: "pointer", fontSize: "14px" }}>
            Cancel
          </button>
          <button
            onClick={() => onConfirm(targetFolderId)}
            disabled={confirmDisabled}
            style={{ ...styles.addButton, padding: "8px 18px", opacity: confirmDisabled ? 0.6 : 1 }}
          >
            {processingFile ? "Processing..." : mode === "move" ? "Move here" : "Copy here"}
          </button>
        </div>
      </div>
    </div>
  );
}
